import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, noop } from 'lodash';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { v4 } from 'uuid';
import { Client } from '../../../client-data/client.model';
import { IDialogData } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import {
  AddClientActionsEnum,
  IAddClientActionTrigger,
  IAddClientData,
} from '../../../document-detail/components/add-client/add-client.component';
import { IError } from '../../../entity-data/entity-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { LocationInfo } from '../../../geolocation-data/models/location-info.model';
import { GeolocationDataService } from '../../../geolocation-data/geolocation-data.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { AddTrackedTimeBuilder } from './add-tracked-time.builder';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import {
  DateTimeService,
  DAY_AS_SECONDS,
  MOMENT_AM_PM_TIME_FORMAT,
} from 'i2g-ng-utils';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { TrackedTimeDataDispatcher } from '../../../tracked-time-data/tracked-time-data.dispatcher';
import { TrackedTimeDataStore } from '../../../tracked-time-data/tracked-time-data.store';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  IToasterConfig,
  IToasterData,
  ToasterTypeEnum,
} from '../../../toaster/toaster.data';
import { MessageToastComponent } from '../../../shared/components/message-toast/message-toast.component';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import { ClientDataDispatcher } from '../../../client-data/client-data.dispatcher';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
} from 'rxjs/operators';

@Component({
  selector: 'app-add-tracked-time',
  templateUrl: 'add-tracked-time.component.html',
  styleUrls: ['add-tracked-time.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AddTrackedTimeBuilder],
})
export class AddTrackedTimeComponent implements OnInit, OnDestroy {
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _addClientActionTriggers: Subject<IAddClientActionTrigger>;
  private _startedTimer: string;
  private _initialTimerValue: number;

  timer: Subscription;
  trackedTime: TrackedTime;
  billing: Billing;
  formKeys: any;
  form: FormGroup;
  documentSettings$: Observable<DocumentPresetSettings>;
  accountSettings$: Observable<Settings>;
  addClientActionTriggers$: Observable<IAddClientActionTrigger>;
  featureToggles$: Observable<Toggles>;
  locationInfo$: Observable<LocationInfo>;
  timerValue: number;
  timerDisplay: string;
  timerDescription: string;
  errorMessage: string;
  startIcon: IIcon;
  stopIcon: IIcon;
  removeIcon: IIcon;
  chevronDownIcon: IIcon;
  errorIcon: IIcon;
  isMinimized: boolean;
  isClientEditMode: boolean;
  inputObject: ITrackingObject;
  popupObject: ITrackingObject;
  trackingScreenName: TrackingScreenNamesEnum;
  discardTrackingAction: ITrackingAction;
  openedAction: ITrackingAction;
  savedAction: ITrackingAction;
  addClientAction: ITrackingAction;
  deleteClientAction: ITrackingAction;
  notesAction: ITrackingAction;
  saveAction: ITrackingAction;
  discardAction: ITrackingAction;
  minimizeAction: ITrackingAction;
  maximizeAction: ITrackingAction;
  startAction: ITrackingAction;
  stopAction: ITrackingAction;
  discardConfirmationDialogId: string;

  constructor(
    private _addTrackedTimeBuilder: AddTrackedTimeBuilder,
    private _trackedTimeDataStore: TrackedTimeDataStore,
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _settingsDataStore: SettingsDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _geolocationDataService: GeolocationDataService,
    private _dateTimeService: DateTimeService,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _translateService: TranslateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _toasterDispatcher: ToasterDispatcher,
    private _dialogService: DialogService,
    private _globalBannerService: GlobalBannerService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this._addClientActionTriggers = new Subject<IAddClientActionTrigger>();

    this.formKeys = this._addTrackedTimeBuilder.FORM_KEYS;
    this.form = this._addTrackedTimeBuilder.buildForm();
    this.startIcon = {
      name: 'start',
      size: IconSizesEnum.large,
    };
    this.stopIcon = {
      name: 'stop',
      size: IconSizesEnum.large,
    };
    this.removeIcon = {
      name: 'close',
      size: IconSizesEnum.small,
    };
    this.chevronDownIcon = {
      name: 'chevron-down',
      size: IconSizesEnum.small,
    };
    this.errorIcon = {
      name: 'error',
      size: IconSizesEnum.small,
    };

    this.discardConfirmationDialogId = v4();
    this.trackingScreenName = TrackingScreenNamesEnum.active_tracked_time;
    this.popupObject = this._trackingObjectBuilder.buildPopup('tracked_time');
    this.openedAction = this._trackingActionBuilder.buildOpened(
      false,
      this.trackingScreenName,
      'unbilled'
    );
    this.savedAction = this._trackingActionBuilder.buildSaved(false);
    this.addClientAction = this._trackingActionBuilder.buildButtonTapped(
      'add_client',
      this.trackingScreenName
    );
    this.deleteClientAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_client',
      this.trackingScreenName
    );
    this.notesAction = this._trackingActionBuilder.buildButtonTapped(
      'notes',
      this.trackingScreenName
    );
    this.saveAction = this._trackingActionBuilder.buildButtonTapped(
      'save',
      this.trackingScreenName
    );
    this.discardAction = this._trackingActionBuilder.buildButtonTapped(
      'discard',
      this.trackingScreenName
    );
    this.minimizeAction = this._trackingActionBuilder.buildButtonTapped(
      'minimize',
      this.trackingScreenName
    );
    this.maximizeAction = this._trackingActionBuilder.buildButtonTapped(
      'maximize',
      this.trackingScreenName
    );
    this.startAction = this._trackingActionBuilder.buildButtonTapped(
      'start',
      this.trackingScreenName
    );
    this.stopAction = this._trackingActionBuilder.buildButtonTapped(
      'stop',
      this.trackingScreenName
    );
    this.discardTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'discard',
      this.trackingScreenName
    );

    this.addClientActionTriggers$ = this._addClientActionTriggers.asObservable();
    this.locationInfo$ = this._geolocationDataService.getLocationInfo();
    this.featureToggles$ = this._featuresDataStore.featureToggles$;
    this.accountSettings$ = this._settingsDataStore.settings$.pipe(
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
    this.documentSettings$ = this._settingsDataStore.settings$.pipe(
      map((settings: Settings) => settings.documentPresetSettings),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    // create/clear a draft tracked time
    this._trackedTimeDataStore
      .getDraftTrackedTime()
      .pipe(takeUntil(this._destroyed))
      .subscribe((trackedTime: TrackedTime) => {
        this.trackedTime = trackedTime;
        this.errorMessage = null;

        if (!trackedTime) {
          this.timerValue = 0;
          this.timerDisplay = this._dateTimeService.formatDuration(
            this.timerValue
          );
          this.timerDescription = null;
          this.isMinimized = false;
          this.form = this._addTrackedTimeBuilder.buildForm();
          this.inputObject = null;

          if (this.timer) {
            this.timer.unsubscribe();

            this.timer = null;
          }
        } else if (!trackedTime.startDate) {
          this.billing = new Billing();
          this.inputObject = this._trackingObjectBuilder.buildInput(
            trackedTime.id,
            trackedTime.localId,
            TrackingInputTypeValuesEnum.tracked_time
          );

          this._trackingDispatcher.trackEvent(
            this.inputObject,
            this.openedAction
          );
        }

        this._changeDetectorRef.markForCheck();
      });

    this._trackedTimeDataStore
      .getDraftTrackedTimeError()
      .pipe(takeUntil(this._destroyed))
      .subscribe((err: IError) => {
        if (err) {
          const { error } = err;
          const toastData: IToasterData = {
            label: error.message,
            type: ToasterTypeEnum.warning,
          };
          const toastConfig: IToasterConfig = {
            type: 'tracked-time-warning',
            component: MessageToastComponent,
            data: toastData,
          };

          this._toasterDispatcher.showToast(toastConfig);
        } else {
          this._toasterDispatcher.clearToast(null);
        }
      });
  }

  /****
   TRACKED TIME
   ****/
  startStopTimer() {
    if (!this.timer) {
      if (!this.trackedTime.startDate) {
        const startDate = this._dateTimeService.getNow(null, true);

        this._setTimerDecription(startDate);

        this._trackedTimeDataDispatcher.setDraftTrackedTime(
          this.trackedTime.setStartDate(startDate)
        );
      }

      this._startedTimer = this._dateTimeService.getNow(null, true);
      this._initialTimerValue = this.timerValue || 0;

      this.timer = timer(1000, 1000)
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => {
          if (this.timerValue < DAY_AS_SECONDS) {
            const timeNow = this._dateTimeService.getNow(null, true);
            const fromTimeToNow = this._dateTimeService.calculateNumberOfUnitsFromTime(
              this._startedTimer,
              timeNow,
              'seconds',
              true
            );
            const totalTime = fromTimeToNow + this._initialTimerValue;

            this.timerValue = Math.min(totalTime, DAY_AS_SECONDS);
            this.timerDisplay = this._dateTimeService.formatDuration(
              this.timerValue
            );
          } else {
            this._stopTimer();
          }

          this._changeDetectorRef.markForCheck();
        });
    } else {
      this._stopTimer();
    }

    this._changeDetectorRef.markForCheck();
  }

  toggleTimer() {
    this.isMinimized = !this.isMinimized;

    if (this.isMinimized) {
      // minimised state
      if (
        this.trackedTime &&
        !this.isClientEditMode &&
        !this.trackedTime.startDate &&
        !this.billing.hasValue() &&
        isEmpty(this.form.get(this.formKeys.NOTES).value)
      ) {
        // clear timer
        this._trackedTimeDataDispatcher.clearDraftTrackedTime();
      } else if (this.trackedTime.startDate) {
        // set long description
        this._setTimerDecription(this.trackedTime.startDate, this.billing.name);
      }
    } else {
      // maximised state
      // display client form
      if (this.isClientEditMode) {
        this._addClientActionTriggers.next({
          action: AddClientActionsEnum.show_form,
        });
      }

      // set short description
      if (this.trackedTime.startDate) {
        this._setTimerDecription(this.trackedTime.startDate);
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  saveTrackedTime() {
    if (this.billing && this.billing.hasValue()) {
      const newTrackedTime = this._addTrackedTimeBuilder.buildTrackedTime(
        this.trackedTime,
        this.timerValue,
        this.billing,
        this.form.get(this.formKeys.NOTES).value
      );

      this._trackedTimeDataDispatcher.clearDraftTrackedTime();

      this._trackedTimeDataDispatcher
        .saveNewEntity(newTrackedTime)
        .pipe(takeUntil(this._destroyed))
        .subscribe(
          (trackedTime: TrackedTime) => {
            const toastData: IToasterData = {
              label: this._translateService.instant(
                'TRACKED_TIME.SAVED_MESSAGE',
                {
                  clientName: newTrackedTime.billingName,
                }
              ),
            };
            const toastConfig: IToasterConfig = {
              type: 'tracked-time-saved',
              component: MessageToastComponent,
              data: toastData,
            };
            const inputObject: ITrackingObject = this._trackingObjectBuilder.buildInput(
              trackedTime.id,
              trackedTime.localId,
              TrackingInputTypeValuesEnum.tracked_time
            );

            this.errorMessage = null;

            this._toasterDispatcher.showToast(toastConfig);

            this._trackingDispatcher.trackEvent(inputObject, this.savedAction);
          },
          (err: IError) =>
            this._globalBannerService.showSystemErrorBanner(err.error)
        );
    } else {
      this.errorMessage = this._translateService.instant(
        'TRACKED_TIME.ERROR.SAVE_WITHOUT_A_CLIENT'
      );
    }
  }

  discardTrackedTime() {
    const newTrackedTime = this._addTrackedTimeBuilder.buildTrackedTime(
      this.trackedTime,
      this.timerValue,
      this.billing,
      this.form.get(this.formKeys.NOTES).value
    );

    if (this.timerValue || newTrackedTime.hasValue()) {
      this._dialogService.openConfirmDialog(this.discardConfirmationDialogId);
    } else {
      this._trackedTimeDataDispatcher.clearDraftTrackedTime();
    }
  }

  onDiscardConfirmClose({ result }: IDialogData) {
    if (result) {
      this._trackedTimeDataDispatcher.clearDraftTrackedTime();
    }
  }

  /****
   CLIENT
   ****/
  onClientEditMode(flag: boolean) {
    this.isClientEditMode = flag;

    if (flag) {
      this.errorMessage = null;
    }

    this._changeDetectorRef.markForCheck();
  }

  updateClient(addClientData: IAddClientData) {
    const billingInfo = addClientData.billingInfo;
    const reference = addClientData.reference;
    const clientAssignedAction: ITrackingAction = this._trackingActionBuilder.buildInputAssigned(
      reference.id,
      reference.referenceLocalId,
      TrackingInputTypeValuesEnum.client
    );

    this._trackingDispatcher.trackEvent(this.inputObject, clientAssignedAction);

    this.billing = this._addTrackedTimeBuilder.buildBilling(
      reference,
      billingInfo.get(Billing.KEYS.NAME),
      billingInfo.get(Billing.KEYS.EMAIL),
      billingInfo.get(Billing.KEYS.ADDRESS)
    );

    this._changeDetectorRef.markForCheck();
  }

  saveClient(client: Client) {
    this._clientDataDispatcher
      .saveNewEntity(client)
      .pipe(
        takeUntil(this._destroyed),
        take(1),
        filter((x) => !!x)
      )
      .subscribe((client: Client) => {
        const reference: Reference = this._addTrackedTimeBuilder.buildClientReference(
          client
        );

        this.billing = this._addTrackedTimeBuilder.buildBilling(
          reference,
          client.billingName,
          client.email,
          client.billingAddress
        );

        this._changeDetectorRef.markForCheck();
      }, noop);
  }

  deleteClient() {
    if (this.billing.hasValue()) {
      const clientRef = this.billing.reference;

      if (clientRef) {
        const clientUnassignedAction: ITrackingAction = this._trackingActionBuilder.buildInputUnassigned(
          clientRef.id,
          clientRef.referenceLocalId,
          TrackingInputTypeValuesEnum.client
        );

        this._trackingDispatcher.trackEvent(
          this.inputObject,
          clientUnassignedAction
        );
      }
    }

    this.billing = new Billing();

    this._addClientActionTriggers.next({
      action: AddClientActionsEnum.close_form,
    });

    this.onClientEditMode(false);
  }

  private _stopTimer() {
    this.timer.unsubscribe();

    this._startedTimer = null;

    this.timer = null;
  }

  private _setTimerDecription(startDate: string, clientName?: string) {
    const localDay = this._dateTimeService.convertToLocal(startDate);
    const startedDay = this._friendlyDatePipe.transform(localDay, false, 1, 0);
    const startedTime = this._dateTimeService.formatDate(
      startDate,
      MOMENT_AM_PM_TIME_FORMAT
    );
    const daysDifference = this._dateTimeService.calculateNumberOfUnitsFromDate(
      localDay,
      this._dateTimeService.getDateString(),
      'day',
      'days',
      false
    );

    this.timerDescription = isEmpty(clientName)
      ? this._translateService.instant('TRACKED_TIME.TIMER.SHORT_DESCRIPTION', {
          startedDay:
            daysDifference < -1 ? startedDay : startedDay.toLowerCase(),
          startedTime,
        })
      : this._translateService.instant('TRACKED_TIME.TIMER.LONG_DESCRIPTION', {
          startedDay:
            daysDifference < -1 ? startedDay : startedDay.toLowerCase(),
          startedTime,
          clientName,
        });
  }

  ngOnDestroy() {
    this._dialogService.closeConfirmDialog(this.discardConfirmationDialogId);
    this._trackedTimeDataDispatcher.clearDraftTrackedTime();

    if (this.timer) {
      this.timer.unsubscribe();
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
